'use client'

import type { SVSPayload } from '../visualizations/svs-types'
import { SVSRenderer } from '../visualizations/SVSRenderer'
import { TextBlock } from './TextBlock'

interface VisualizationBlockProps {
  /** Raw SVS payload (see VISUALIZATION_SPEC.md) */
  payload: Record<string, unknown>
  /** Markdown fallback for unsupported or not-yet-implemented types */
  textFallback?: string
}

/**
 * Bridge between the content block model and the SVS visualization system.
 * Validates the payload has the minimum required shape, then delegates to SVSRenderer.
 * Falls back to textFallback when the payload is malformed.
 */
export function VisualizationBlock({ payload, textFallback }: VisualizationBlockProps) {
  const fallback = textFallback || (typeof payload.textFallback === 'string' ? payload.textFallback : undefined)

  // Validate minimum SVS shape: must have version and type
  if (typeof payload.version === 'string' && typeof payload.type === 'string' && payload.data) {
    return <SVSRenderer payload={payload as unknown as SVSPayload} />
  }

  // Payload doesn't look like valid SVS — render fallback
  if (fallback) {
    return (
      <div className="not-prose my-4 rounded-lg border border-border bg-card p-4">
        <TextBlock content={fallback} />
      </div>
    )
  }

  return (
    <div className="not-prose my-4 rounded-lg border border-border bg-card p-4">
      <p className="text-sm text-muted-foreground italic">
        Visualization data is not available.
      </p>
    </div>
  )
}
